/**
 * Mapping I/O: serialize/deserialize MappingResult to/from JSON.
 * Compatible with the Python cw-cartographer mapping format (v1.0).
 */

import { BoneProperties, RotationLimits, type Skeleton } from '../skeleton/core';
import { JOINT_OFFSETS_IO_KEY } from './jointOffsets';
import {
  DEFAULT_CHARACTER_FORWARD_AXIS,
  DEFAULT_CHARACTER_UP_AXIS,
  characterAxisFromMappingJson,
} from '../schema/characterAxes';
import { ChainMapping } from '../schema/mapping';
import {
  getEffectiveSolverProperties,
  getEffectiveSolverType,
  resolveSolverTypeForPropertiesParsing,
  solverPropertiesFromDict,
} from '../schema/solver';
import { type ScoreBreakdown } from '../recognize/scoring';
import { MappingResult } from '../recognize/config';

const FORMAT_VERSION = '1.0';

function mapOptionalBoneString(
  v: string | null | undefined,
  mapFn: (boneRef: string) => string,
): string | null {
  if (v == null || v === '') return v ?? null;
  return mapFn(v);
}

/**
 * Remap every bone ref string in a mapping (roles/chains/excluded/ioExtras joint_offsets, etc.).
 * Used when importing JSON (source refs -> internal refs) or round-tripping tests.
 */
export function remapMappingBoneNames(
  mapping: MappingResult,
  mapFn: (boneRef: string) => string,
): MappingResult {
  const remapChain = (cr: ChainMapping): ChainMapping =>
    new ChainMapping({
      name: cr.name,
      startBone: mapFn(cr.startBone),
      endBone: mapFn(cr.endBone),
      boneNames: cr.boneNames.map(mapFn),
      ikGoal: mapOptionalBoneString(cr.ikGoal, mapFn),
      confidence: cr.confidence,
      solverType: cr.solverType,
      solverParams: { ...cr.solverParams },
      solverProperties: cr.solverProperties,
      legType: cr.legType,
    });

  const chainResults: Record<string, ChainMapping> = {};
  for (const [cn, cr] of Object.entries(mapping.chainResults)) {
    chainResults[cn] = remapChain(cr);
  }

  const roleToBone = Object.fromEntries(
    Object.entries(mapping.roleToBone).map(([role, bone]) => [role, mapFn(bone)]),
  );
  const chains = Object.fromEntries(
    Object.entries(mapping.chains).map(([cn, boneList]) => [cn, boneList.map(mapFn)]),
  );

  return new MappingResult({
    roleToBone,
    roleToConfidence: { ...mapping.roleToConfidence },
    chains,
    chainResults,
    warnings: [...mapping.warnings],
    explain: { ...mapping.explain },
    presetName: mapping.presetName,
    presetId: mapping.presetId,
    detectedFeatures: [...mapping.detectedFeatures],
    excludedBones: mapping.excludedBones.map(mapFn),
    candidates: { ...mapping.candidates },
    roleToDetectionMethod: { ...mapping.roleToDetectionMethod },
    sideConvention: mapping.sideConvention,
    namingMode: mapping.namingMode,
    forwardAxis: mapping.forwardAxis,
    upAxis: mapping.upAxis,
    ioExtras: remapJointOffsetsIoKeys(mapping.ioExtras, mapFn),
  });
}

function remapJointOffsetsIoKeys(
  ioExtras: Record<string, unknown>,
  mapFn: (boneRef: string) => string,
): Record<string, unknown> {
  const raw = ioExtras[JOINT_OFFSETS_IO_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...ioExtras };
  }
  const offsets = raw as Record<string, unknown>;
  const nextOffsets: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(offsets)) {
    nextOffsets[mapFn(k)] = v;
  }
  return {
    ...ioExtras,
    [JOINT_OFFSETS_IO_KEY]: nextOffsets,
  };
}

/** Top-level keys consumed by mappingFromDict; anything else is preserved in MappingResult.ioExtras. */
const RESERVED_MAPPING_TOP_LEVEL_KEYS = new Set([
  'format_version',
  'role_to_bone',
  'role_to_confidence',
  'chains',
  'info',
  'excluded_bones',
  'chain_config',
  'chain_solvers',
  'chain_overrides',
  'explain',
  'bone_properties',
  'rotation_limits',
  'forward',
  'up',
]);

export function mappingToDict(
  mapping: MappingResult,
  options?: {
    includeConfidence?: boolean;
    includeExplain?: boolean;
    skeleton?: Skeleton | null;
    /** When false, emit internal refs even if skeleton is set. Default true. */
    useSkeletonSourceNames?: boolean;
  },
): Record<string, unknown> {
  const {
    includeConfidence,
    includeExplain,
    skeleton,
    useSkeletonSourceNames = true,
  } = options ?? {};

  const boneRefOut =
    skeleton && useSkeletonSourceNames
      ? (internalRef: string) =>
          skeleton.exportRefForInternalRef(internalRef, {
            useSourceNames: true,
          }) ?? internalRef
      : (internalRef: string) => internalRef;

  const data: Record<string, unknown> = {
    format_version: FORMAT_VERSION,
    forward: mapping.forwardAxis,
    up: mapping.upAxis,
    role_to_bone: Object.fromEntries(
      Object.entries(mapping.roleToBone).map(([role, bone]) => [
        role,
        boneRefOut(bone),
      ]),
    ),
    chains: Object.fromEntries(
      Object.entries(mapping.chains).map(([chainName, boneList]) => [
        chainName,
        boneList.map(boneRefOut),
      ]),
    ),
    info: mapping.warnings,
    excluded_bones: mapping.excludedBones.length
      ? [...new Set(mapping.excludedBones.map(boneRefOut))].sort()
      : [],
  };

  if (includeConfidence && Object.keys(mapping.roleToConfidence).length > 0) {
    data.role_to_confidence = mapping.roleToConfidence;
  }

  if (mapping.chains && Object.keys(mapping.chains).length > 0) {
    const chainConfig: Record<string, Record<string, unknown>> = {};
    for (const chainName of Object.keys(mapping.chains)) {
      const entry: Record<string, unknown> = {};
      const cr = mapping.chainResults[chainName];
      const effectiveSolverType = getEffectiveSolverType(
        chainName,
        cr?.solverType ?? null,
      );

      entry.solver = effectiveSolverType;
      if (cr?.solverParams && Object.keys(cr.solverParams).length > 0) {
        Object.assign(entry, cr.solverParams);
      }
      entry.solverProps = getEffectiveSolverProperties(
        effectiveSolverType,
        cr?.solverProperties ?? null,
      ).toDict();

      chainConfig[chainName] = entry;
    }
    data.chain_config = chainConfig;
  }

  if (includeExplain && mapping.explain) {
    const explain: Record<string, Record<string, number>> = {};
    for (const [role, bd] of Object.entries(mapping.explain)) {
      explain[role] = {
        topology: bd.topology,
        symmetry: bd.symmetry,
        direction: bd.direction,
        length_ratio: bd.lengthRatio,
        naming: bd.naming,
        depth: bd.depth,
        total: bd.total,
      };
    }
    data.explain = explain;
  }

  if (skeleton) {
    const boneProps: Record<string, Record<string, unknown>> = {};
    for (const bone of skeleton.bones) {
      if (bone.properties !== null) {
        const boneRef = skeleton.refForBoneIndex(bone.index, {
          useSourceNames: useSkeletonSourceNames,
        });
        boneProps[boneRef] = bone.properties.toDict();
      }
    }
    if (Object.keys(boneProps).length > 0) {
      data.bone_properties = boneProps;
    }
  }

  const extrasForExport =
    skeleton && useSkeletonSourceNames
      ? remapJointOffsetsIoKeys(mapping.ioExtras, boneRefOut)
      : { ...mapping.ioExtras };

  for (const [key, value] of Object.entries(extrasForExport)) {
    if (!(key in data)) {
      data[key] = value;
    }
  }

  return data;
}

export function mappingFromDict(data: Record<string, unknown>): {
  mapping: MappingResult;
  boneProperties: Record<string, BoneProperties>;
} {
  const formatVersion = data.format_version;
  if (!formatVersion) {
    throw new Error("Mapping JSON missing required 'format_version'");
  }

  const roleToBone = (data.role_to_bone as Record<string, string>) ?? {};
  const roleToConfidence =
    (data.role_to_confidence as Record<string, number>) ?? {};
  const chains = (data.chains as Record<string, string[]>) ?? {};
  const warnings = (data.info as string[]) ?? [];
  const excludedBones = (data.excluded_bones as string[]) ?? [];

  const chainConfigRaw = (data.chain_config as Record<string, Record<string, unknown>>) ?? {};
  const chainSolversRaw = (data.chain_solvers as Record<string, string>) ?? {};
  const chainOverridesRaw = (data.chain_overrides as Record<string, Record<string, unknown>>) ?? {};

  const chainResults: Record<string, ChainMapping> = {};

  for (const [chainName, configEntry] of Object.entries(chainConfigRaw)) {
    const boneNames = chains[chainName] ?? [];
    const solverType = configEntry.solver as string | undefined;
    const solverParams: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(configEntry)) {
      if (k !== 'solver' && k !== 'solverProps') solverParams[k] = v;
    }
    const spData = configEntry.solverProps as Record<string, unknown> | undefined;
    const solverTypeForProperties = resolveSolverTypeForPropertiesParsing(
      chainName,
      solverType ?? null,
      spData,
    );
    const solverProperties = spData
      ? solverPropertiesFromDict(solverTypeForProperties, spData)
      : null;

    chainResults[chainName] = new ChainMapping({
      name: chainName,
      startBone: boneNames[0] ?? '',
      endBone: boneNames[boneNames.length - 1] ?? '',
      boneNames,
      solverType: solverType ?? null,
      solverParams,
      solverProperties,
    });
  }

  for (const [chainName, solverType] of Object.entries(chainSolversRaw)) {
    if (!(chainName in chainResults)) {
      const boneNames = chains[chainName] ?? [];
      chainResults[chainName] = new ChainMapping({
        name: chainName,
        startBone: boneNames[0] ?? '',
        endBone: boneNames[boneNames.length - 1] ?? '',
        boneNames,
        solverType,
      });
    }
  }

  for (const [chainName, overrideData] of Object.entries(chainOverridesRaw)) {
    if (!(chainName in chainResults)) {
      const boneNames = chains[chainName] ?? [];
      chainResults[chainName] = new ChainMapping({
        name: chainName,
        startBone: boneNames[0] ?? '',
        endBone: boneNames[boneNames.length - 1] ?? '',
        boneNames,
        solverType: (overrideData.solver_type as string) ?? null,
      });
    }
  }

  const explainRaw = (data.explain as Record<string, Record<string, number>>) ?? {};
  const explain: Record<string, ScoreBreakdown> = {};
  for (const [role, bd] of Object.entries(explainRaw)) {
    explain[role] = {
      topology: bd.topology ?? 0,
      symmetry: bd.symmetry ?? 0,
      direction: bd.direction ?? 0,
      lengthRatio: bd.length_ratio ?? 0,
      naming: bd.naming ?? 0,
      depth: bd.depth ?? 0,
      total: bd.total ?? 0,
    };
  }

  const boneProperties: Record<string, BoneProperties> = {};
  const bonePropsRaw = (data.bone_properties as Record<string, Record<string, unknown>>) ?? {};
  if (Object.keys(bonePropsRaw).length > 0) {
    for (const [boneName, propsData] of Object.entries(bonePropsRaw)) {
      boneProperties[boneName] = BoneProperties.fromDict(propsData);
    }
  } else {
    const rotLimitsRaw = (data.rotation_limits as Record<string, Record<string, unknown>>) ?? {};
    for (const [boneName, limitsData] of Object.entries(rotLimitsRaw)) {
      boneProperties[boneName] = new BoneProperties(
        RotationLimits.fromDict(limitsData),
      );
    }
  }

  const ioExtras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!RESERVED_MAPPING_TOP_LEVEL_KEYS.has(key)) {
      ioExtras[key] = value;
    }
  }

  const forwardAxis =
    characterAxisFromMappingJson(data.forward) ??
    DEFAULT_CHARACTER_FORWARD_AXIS;
  const upAxis =
    characterAxisFromMappingJson(data.up) ?? DEFAULT_CHARACTER_UP_AXIS;

  const mapping = new MappingResult({
    roleToBone,
    roleToConfidence,
    chains,
    chainResults,
    warnings,
    explain,
    excludedBones,
    forwardAxis,
    upAxis,
    ioExtras,
  });

  return { mapping, boneProperties };
}

export function exportMappingJson(
  mapping: MappingResult,
  skeleton?: Skeleton | null,
): string {
  const data = mappingToDict(mapping, {
    includeConfidence: true,
    skeleton,
  });
  return JSON.stringify(data, null, 2);
}

export function importMappingJson(
  jsonStr: string,
): { mapping: MappingResult; boneProperties: Record<string, BoneProperties> } {
  const data = JSON.parse(jsonStr);
  return mappingFromDict(data);
}
